import {
  Component,
  Input,
  SimpleChanges,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
} from "@angular/core";
import { TPRDataService } from "../tpr-data.service";
import { Column, SubColumn } from "../tpr.model";
import { MONTH_DATE_ABB_YEAR_FORMAT } from "src/app/common-constants";
import dayjs from "dayjs";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { formatRevSupCell } from "src/app/utils/comparison-grid";
import { formatMCapAndEVCell } from "src/app/utils/common";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { Store } from "@ngrx/store";
import { environment } from "../../../environments/environment";

import {
  computeVsPeersGridAverage,
  computeVsPeersGridMedian,
} from "../tpr-utils";
import { SUB_COLUMNS_WITHOUT_LOADER } from "../tpr-constants";
@Component({
  selector: "app-tpr-peers-grid-wrapper",
  templateUrl: "./tpr-peers-grid-wrapper.component.html",
  styleUrls: ["./tpr-peers-grid-wrapper.component.scss"],
})
export class TprPeersGridWrapperComponent implements OnInit, OnDestroy {
  @Input() primaryTicker = "";
  @Input() meta: any;
  @Input() isMetaInProgress: boolean;
  @Input() isTabVisible: boolean;
  @Input() gridContainerClassName = "industry-valuation-grid";
  @Input() showGridPagination = false;
  @Input() sortByColumnId: string;
  @Output() updateTickerOrder: EventEmitter<any> = new EventEmitter();
  @Output() onDeleteTicker: EventEmitter<any> = new EventEmitter();

  // use this._meta always in this component, as we modify input meta to append period with id in colInfo to make id unique
  _meta: any;
  companyColIds = ["C0", "C1"];
  isDataInProgress = false;
  gridData: any;
  rawData: any; // row's data in raw object form
  pendingApiCallsCount = 0;
  columnsPerAPIType = {};
  batchStatus = new BehaviorSubject<string>(null); //'running' | 'completed';
  dataCalls: Array<Subscription>;
  batchSubscription: Subscription;
  BATCH_FIRST_ITERATION = "first";
  BATCH_SUBSEQUENT_ITERATION = "subsequent";
  MARKET_CAP = "mCap";
  EV = "ev";
  EPS_GROWTH_COL_ID = "5656|NTM";
  CURRENT_IMPLIED_RETURN_COL_ID = "83";
  MULTIPLE_EXPANSION_COL_ID = "82";
  marketDataApiTypes = ["price_performance", "price"];
  tprApiTypes = ["tpr"];
  singleColumnApiTypes = ["mcap", "ev"];
  ratingColorConfig = {
    buy: "green",
    hold: "yellow",
    sell: "red",
  };
  assets = environment.assetUrl;

  //subscribers
  globalAppSettingSubscriber: Subscription;

  constructor(
    private tprDataService: TPRDataService,
    private store: Store<AppState>,
  ) {}

  ngOnInit() {
    this.globalAppSettingSubscriber = this.store
      .select(
        stateGetter("globalHeader", "globalAppSettings", "period_settings"),
      )
      .subscribe((data) => {
        if (data && this.meta) {
          this.makeDataCalls();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.meta &&
      changes.meta.currentValue !== changes.meta.previousValue
    ) {
      this.makeDataCalls();
    }
  }

  makeDataCalls() {
    if (this.dataCalls && this.dataCalls.length) {
      this.dataCalls.forEach((dataCall) => {
        dataCall.unsubscribe && dataCall.unsubscribe();
      });
    }
    const rawDataAndColInfo = this.createRowDataAndColInfo(this.meta);
    this.rawData = rawDataAndColInfo.row;
    this._meta = { ...this.meta, colInfo: rawDataAndColInfo.colInfo };

    this.gridData = {
      colInfo: rawDataAndColInfo.colInfo,
      columns: this._meta.columns,
      rowData: [],
      footerData: {
        average: {},
        median: {},
        subColumnsWithoutLoader: SUB_COLUMNS_WITHOUT_LOADER,
      },
    };
    this.pushToRowData();
    const apiTypeList = this._meta.apiTypeList;
    apiTypeList.forEach((type) => {
      const apiType = type.api_type;
      this.columnsPerAPIType[apiType] = {
        columns: this.getColumnsForType(apiType, this._meta),
        batchSize: type.batch_size,
        callCount: type.call_count,
      };
    });
    this.batchStatus.next("firstBatchRunning");
    this.dataCalls = this.makeParamsAndCall(
      this._meta,
      this.BATCH_FIRST_ITERATION,
    );
    this.batchSubscription = this.batchStatus
      .pipe(take(2))
      .subscribe((status) => {
        if (status === "firstBatchCompleted") {
          this.makeParamsAndCall(this._meta, this.BATCH_SUBSEQUENT_ITERATION);
        }
      });
    // forkJoin(dataCalls).subscribe(() => {
    //   this.isDataInProgress = false
    //   dataCalls = this.makeParamsAndCall(this._meta, this.BATCH_SUBSEQUENT_ITERATION);
    //   forkJoin(dataCalls).subscribe(() => {
    //     this.isDataInProgress = false
    //   })
    // })
  }

  /**
   * Creates data needed by grid in raw form.
   * grid needs data in the form of array of objects, each array being row and
   * each object in array represents a cell. Whereas this function creates data
   * in the form of object of object with tickerId as the key of object.
   * {
   *  AAL : { rowObject }
   * }
   * This makes it easy to map response received from api into grid data.
   * Later we can convert this object of object to array of object.
   * @returns
   */
  createRowDataAndColInfo(meta) {
    const colInfoT = JSON.parse(JSON.stringify(meta.colInfo));
    const colInfo = meta.colInfo;
    const columns = {};
    Object.keys(meta.colInfo).forEach((colId) => {
      const subCols = colInfo[colId].subheaders_info;
      if (subCols && subCols.length) {
        const subColumns = {};

        subCols.forEach((subCol, index) => {
          if (
            this.marketDataApiTypes.indexOf(subCol.api_type) !== -1 ||
            this.tprApiTypes.indexOf(subCol.api_type) !== -1
          ) {
            subColumns[subCol.id] = {
              isLoading: true,
              apiType: subCol.api_type,
            };
          } else {
            // for non-market data columns, append the period info into subcolumn id
            // so that its easier to get perid info while mapping api response, otherwise
            // there will be an iteration for each period, each ticker.
            const id = `${subCol.id}|${subCol.extras_info[0].other_period}`;
            subColumns[id] = {
              isLoading: true,
              apiType: subCol.api_type,
            };
            colInfoT[colId].subheaders_info[index].id = id;
          }
        });
        columns[colId] = { subColumns };
      } else {
        // else will run for market cap and ev columns, as they don't have sub columns
        columns[colId] = {
          isLoading: true,
        };
      }
    });
    const row = {};
    meta.tickerInfo.forEach((tickerData) => {
      row[tickerData.ticker] = JSON.parse(JSON.stringify(columns));
      row[tickerData.ticker][this.companyColIds[0]] = tickerData.ticker;
      row[tickerData.ticker][this.companyColIds[1]] =
        tickerData.name || tickerData.full_name;
      row[tickerData.ticker]["ticker"] = tickerData.ticker;
      row[tickerData.ticker]["cid"] = tickerData.companyid || tickerData.cid;
      row[tickerData.ticker]["tickerDeleteDisabled"] =
        tickerData.ticker === this.primaryTicker;
    });
    return { row, colInfo: colInfoT };
  }

  /**
   * finds all the columns or subColumns for the given apiType
   * @param type
   * @param meta
   * @returns
   */
  getColumnsForType(type, meta) {
    const colIds = Object.values(meta.colInfo)
      .filter((col) => {
        return (col as any).api_type === type;
      })
      .map((col: any) => col.id);
    const subColIds = (
      Object.values(meta.colInfo).map((col) => {
        return (col as any).subheaders_info.filter(
          (subCol) => (subCol as any).api_type === type,
        );
      }) as any
    )
      .flat()
      .map((subCol: any) => subCol.id);
    return [...colIds, ...subColIds];
  }

  mapSubColumnMarketData(data, apiType) {
    Object.keys(data).forEach((ticker) => {
      if (Object.keys(data[ticker].market_data).length) {
        const marketData = data[ticker].market_data;
        const marketDataKeys = Object.keys(marketData).filter(
          (mKey) => mKey.indexOf("|data_exists") !== -1,
        );
        marketDataKeys.forEach((dataExistKey) => {
          const mKey = dataExistKey.split("|")[0];
          Object.keys(this.rawData[ticker]).forEach((colId) => {
            // for market cap and EV columns there is no sub column, so its data has to be put in parent
            // column id inside rawData, i.e. C2 and C3 will hold data
            if (this.singleColumnApiTypes.indexOf(apiType) !== -1) {
              if (
                this._meta.colInfo[colId] &&
                this._meta.colInfo[colId].id &&
                this._meta.colInfo[colId].id.toString() === mKey.toString()
              ) {
                if (marketData[dataExistKey]) {
                  this.rawData[ticker][colId] = {
                    isLoading: false,
                    ...marketData[mKey],
                    formatted: formatMCapAndEVCell(
                      marketData[mKey].values,
                      marketData[`${mKey}|C`],
                    ),
                    raw: marketData[mKey].values,
                    lastDate: marketData[mKey].last_date
                      ? dayjs(marketData[mKey].last_date).format(
                          MONTH_DATE_ABB_YEAR_FORMAT,
                        )
                      : "",
                    exchangeFullName: marketData[mKey].exchange_full_name,
                    currency: marketData[`${mKey}|C`],
                    isForecast: marketData[`${mKey}|is_forcast`],
                    rd: marketData[`${mKey}|RD`],
                    viewBy: "values",
                  };
                } else {
                  this.rawData[ticker][colId] = {
                    isLoading: false,
                    formatted: "-",
                    raw: undefined,
                    lastDate: "",
                    exchangeFullName: "",
                    currency: "",
                    isForecast: "",
                    rd: "",
                  };
                }
              }
            } else {
              if (
                this.rawData[ticker][colId] &&
                this.rawData[ticker][colId].subColumns
              ) {
                Object.keys(this.rawData[ticker][colId].subColumns).forEach(
                  (subColId) => {
                    if (
                      subColId === mKey &&
                      this.rawData[ticker][colId].subColumns[subColId]
                        .apiType === apiType
                    ) {
                      if (marketData[dataExistKey] && marketData[mKey]) {
                        const lastDateData =
                          (marketData[mKey] && marketData[mKey].last_date) ||
                          "";
                        const exchangeData =
                          (marketData[mKey] &&
                            marketData[mKey].exchange_full_name) ||
                          "";
                        let viewBy = "values";

                        const columnInMeta = this._meta.colInfo[
                          colId
                        ].subheaders_info.find(
                          (subHeader) =>
                            subHeader.id.toString() === subColId.toString(),
                        );
                        if (columnInMeta) {
                          viewBy = columnInMeta.extras_info[0].viewBy;
                        }
                        this.rawData[ticker][colId]["subColumns"][subColId] = {
                          isLoading: false,
                          apiType:
                            this.rawData[ticker][colId]["subColumns"][subColId]
                              .apiType,
                          ...marketData[mKey],
                          lastDate: lastDateData
                            ? dayjs(lastDateData).format(
                                MONTH_DATE_ABB_YEAR_FORMAT,
                              )
                            : "",
                          exchangeFullName: exchangeData,
                          currency: marketData[`${mKey}|C`],
                          isForecast: marketData[`${mKey}|is_forcast`],
                          rd: marketData[`${mKey}|RD`],
                          viewBy,
                        };
                        if (apiType === "price_performance") {
                          this.rawData[ticker][colId]["subColumns"][
                            subColId
                          ].formatted = formatRevSupCell(
                            marketData[mKey].revision,
                            "",
                            "%",
                          );
                          this.rawData[ticker][colId]["subColumns"][
                            subColId
                          ].raw = marketData[mKey].revision;
                        } else {
                          this.rawData[ticker][colId]["subColumns"][
                            subColId
                          ].formatted = formatRevSupCell(
                            marketData[mKey].values,
                            marketData[`${mKey}|C`],
                            "",
                          );
                          this.rawData[ticker][colId]["subColumns"][
                            subColId
                          ].raw = marketData[mKey].values;
                        }
                      } else {
                        this.rawData[ticker][colId]["subColumns"][subColId] = {
                          isLoading: false,
                          apiType:
                            this.rawData[ticker][colId]["subColumns"][subColId]
                              .apiType,
                          formatted: "-",
                          lastDate: "",
                          exchangeFullName: "",
                          currency: "",
                          isForecast: "",
                          rd: "",
                        };
                      }
                    }
                  },
                );
              }
            }
          });
        });
      }
    });
  }

  /**
   *
   * @param ticker
   * @param colId
   * @param subColId
   * @param refinedSubColId
   * @param period
   * @param data
   * @param viewBy
   * assigns values in rawData for non-market data columns
   */
  assignValues(
    ticker,
    colId,
    subColId,
    refinedSubColId,
    period,
    data,
    viewBy,
    unit,
    currency,
  ) {
    const uKey = `${refinedSubColId}|U`,
      mKey = `${refinedSubColId}|M`,
      primarySliKey = `${refinedSubColId}|primarysli`,
      rdKey = `${refinedSubColId}|RD`;
    // currency = `${refinedSubColId}|C`;
    this.rawData[ticker][colId].subColumns[subColId] = {
      isLoading: false,
      rd: data[ticker][rdKey][period],
      currency: currency,
      isForecast: data[ticker][refinedSubColId][period].is_forecast,
      otherInfo: {
        uKey: data[ticker][uKey],
        mKey: data[ticker][mKey],
        primarySliKey: data[ticker][primarySliKey],
      },
      periodValue: period,
      viewBy: viewBy,
    };

    this.rawData[ticker][colId].subColumns[subColId].value =
      data[ticker][refinedSubColId][period][viewBy];
    this.rawData[ticker][colId].subColumns[subColId].raw =
      data[ticker][refinedSubColId][period][viewBy];
    this.rawData[ticker][colId].subColumns[subColId].formatted =
      formatRevSupCell(
        data[ticker][refinedSubColId][period][viewBy],
        currency,
        unit,
      );
  }

  isEPSGrowthColumn(subColId, viewByT) {
    const isCorrectColId = subColId.toString() === this.EPS_GROWTH_COL_ID;
    return isCorrectColId && viewByT === "yoy";
  }

  mapNormalData(data) {
    Object.keys(data).forEach((ticker) => {
      Object.keys(this.rawData[ticker]).forEach((colId) => {
        if (this.rawData[ticker][colId].subColumns) {
          Object.keys(this.rawData[ticker][colId].subColumns).forEach(
            (subColId) => {
              if (subColId.indexOf("|") !== -1) {
                const refinedSubColId = subColId.split("|")[0];
                const period = subColId.split("|")[1];

                // let subCol= this._meta.colInfo[colId].subheaders_info.find((subH) => subH.id.toString() === subColId);
                // if(!subCol) {console.log(colId); console.log(ticker)}
                // let period = subCol.extras_info[0].other_period;
                // let valueKey = this._meta.colIno[colId].subheaders_info.extras_info.view_by
                const dataExists = `${refinedSubColId}|data_exists`;
                if (data[ticker][dataExists]) {
                  if (
                    data[ticker][refinedSubColId] &&
                    data[ticker][refinedSubColId][period]
                  ) {
                    // special handling for EPS 1-year average growth column
                    // as it needs data to be read from yoy instead of values field
                    let viewBy = "values";
                    const columnInMeta = this._meta.colInfo[
                      colId
                    ].subheaders_info.find(
                      (subHeader) =>
                        subHeader.id.toString() === subColId.toString(),
                    );
                    if (columnInMeta) {
                      viewBy = columnInMeta.extras_info[0].viewBy;
                    }
                    if (this.isEPSGrowthColumn(subColId, viewBy)) {
                      // check if there is value for yoy/values, then only assign it, otherwise don't do anything
                      if (data[ticker][refinedSubColId][period]["yoy"]) {
                        this.assignValues(
                          ticker,
                          colId,
                          subColId,
                          refinedSubColId,
                          period,
                          data,
                          "yoy",
                          "%",
                          "",
                        );
                      }
                    } else {
                      const currency = `${refinedSubColId}|C`;
                      let valueOfCurrency = data[ticker][currency];
                      let unit = "";
                      if (
                        refinedSubColId === this.MULTIPLE_EXPANSION_COL_ID ||
                        refinedSubColId === this.CURRENT_IMPLIED_RETURN_COL_ID
                      ) {
                        valueOfCurrency = "";
                        unit = "%";
                      }

                      this.assignValues(
                        ticker,
                        colId,
                        subColId,
                        refinedSubColId,
                        period,
                        data,
                        viewBy,
                        unit,
                        valueOfCurrency,
                      );
                    }
                  }
                }
                // if dataExists is there and its value is false, then only mark with hyphen
                // so "if(data[ticker][dataExists] === false)" is important
                // can't do "if(!data[ticker][dataExists])"
                else if (data[ticker][dataExists] === false) {
                  this.rawData[ticker][colId].subColumns[subColId] = {
                    isLoading: false,
                    value: "",
                    raw: undefined,
                    formatted: "-",
                  };
                }
              }
            },
          );
        }
      });
    });
  }

  mapTargetPriceData(data) {
    Object.keys(data).forEach((ticker) => {
      Object.keys(this.rawData[ticker]).forEach((colId) => {
        const subColumns = this.rawData[ticker][colId].subColumns;
        if (this.rawData[ticker][colId].subColumns) {
          Object.keys(subColumns).forEach((subColId) => {
            if (subColId === "target_price") {
              if (!data[ticker]["has_current_consensus"]) {
                this.rawData[ticker][colId]["subColumns"][
                  "target_price"
                ].isLoading = false;
                this.rawData[ticker][colId]["subColumns"]["target_price"] = {
                  apiType:
                    this.rawData[ticker][colId]["subColumns"]["target_price"]
                      .apiType, // take existing apiType
                  isLoading: false,
                  raw: 0,
                  formatted: formatRevSupCell(
                    data[ticker]["target_price"],
                    data[ticker]["target_price|C"],
                    "",
                  ),
                  rd: data[ticker]["target_price|RD"],
                  periodValue: data[ticker]["period"],
                  viewBy: "values",
                };
              } else {
                this.rawData[ticker][colId]["subColumns"]["target_price"] = {
                  apiType:
                    this.rawData[ticker][colId]["subColumns"]["target_price"]
                      .apiType, // take existing apiType
                  isLoading: false,
                  raw: data[ticker]["target_price"],
                  formatted: formatRevSupCell(
                    data[ticker]["target_price"],
                    data[ticker]["target_price|C"],
                    "",
                  ),
                  rd: data[ticker]["target_price|RD"],
                  periodValue: data[ticker]["period"],
                  viewBy: "values",
                };
              }
            }
            if (subColId === "consensus_values") {
              if (!data[ticker]["has_current_consensus"]) {
                this.rawData[ticker][colId]["subColumns"]["consensus_values"] =
                  {
                    isLoading: false,
                    label: "-",
                    value: "-",
                    raw: undefined,
                    bgColor: "",
                  };
              } else {
                const bgColor =
                  data[ticker]["rating"] &&
                  data[ticker]["rating"].toLowerCase();
                this.rawData[ticker][colId]["subColumns"]["consensus_values"] =
                  {
                    isLoading: false,
                    label: data[ticker]["rating"].toUpperCase(),
                    value: formatRevSupCell(
                      data[ticker]["rating_numeric"],
                      "",
                      "",
                    ),
                    raw: data[ticker]["rating_numeric"],
                    bgColor: this.ratingColorConfig[bgColor],
                  };
              }
            }

            if (subColId === "rating_distribution") {
              if (!data[ticker]["has_current_consensus"]) {
                this.rawData[ticker][colId]["subColumns"][
                  "rating_distribution"
                ] = {
                  isLoading: false,
                  total: 0,
                  buy: { abs: 0, perc: `0%` },
                  sell: { abs: 0, perc: `0%` },
                  hold: { abs: 0, perc: `0%` },
                };
              } else {
                const buyRating = data[ticker].broker_with_buy_rating || 0;
                const sellRating = data[ticker].broker_with_sell_rating || 0;
                const holdRating = data[ticker].broker_with_hold_rating || 0;
                const total = buyRating + sellRating + holdRating;
                const buyPerc = total
                  ? ((buyRating / total) * 100).toFixed(2)
                  : 0;
                const sellPerc = total
                  ? ((sellRating / total) * 100).toFixed(2)
                  : 0;
                const holdPerc = total
                  ? ((holdRating / total) * 100).toFixed(2)
                  : 0;
                this.rawData[ticker][colId]["subColumns"][
                  "rating_distribution"
                ] = {
                  isLoading: false,
                  total: total,
                  buy: { abs: buyRating, perc: `${buyPerc}%` },
                  sell: { abs: sellRating, perc: `${sellPerc}%` },
                  hold: { abs: holdRating, perc: `${holdPerc}%` },
                };
              }
            }
          });
        }
      });
    });
  }

  setDataInProgressFlag() {
    this.pendingApiCallsCount--;
    if (this.pendingApiCallsCount === 0) {
      this.batchStatus.pipe(take(1)).subscribe((status) => {
        if (status === "firstBatchRunning") {
          this.batchStatus.next("firstBatchCompleted");
        } else if (status === "firstBatchCompleted") {
          this.isDataInProgress = false;
          this.setLoadingFalseAndcomputeAvgAndMedian();
        }
      });
    }
    this.batchStatus.pipe(take(1)).subscribe((status) => {
      if (status === "secondBatchCompleted") {
        this.isDataInProgress = false;
        this.setLoadingFalseAndcomputeAvgAndMedian();
      }
    });
  }

  ngOnDestroy() {
    this.batchSubscription &&
      this.batchSubscription.unsubscribe &&
      this.batchSubscription.unsubscribe();

    if (this.globalAppSettingSubscriber) {
      this.globalAppSettingSubscriber.unsubscribe();
    }
  }

  /**
   * Makes call to api and returns the call observable, but also subscribes to the observable to do
   * post processing of response
   * @param urlParams string
   * @param apiType string
   * @returns Observable
   */
  dataCall(urlParams: string, apiType: string): Observable<any> {
    this.pendingApiCallsCount++;
    const call = this.tprDataService.getVsPeersData(urlParams);

    call.subscribe((dataT: any) => {
      const data = dataT.data;
      if (
        this.marketDataApiTypes.indexOf(apiType) !== -1 ||
        this.singleColumnApiTypes.indexOf(apiType) !== -1
      ) {
        this.mapSubColumnMarketData(data, apiType);
      } else if (this.tprApiTypes.includes(apiType)) {
        this.mapTargetPriceData(data);
      } else {
        this.mapNormalData(data);
      }
      this.pushToRowData();
      this.setDataInProgressFlag();
    });
    return call;
  }
  /**
   * prepare url params and call dataCall function to fire multiple api calls,
   * api calls are divided based on batchSize (i.e. number of tickers in each call) and number of calls
   * in each batchIteration. e.g. for colA make  api calls for 3 tickers each in first iteration. It means
   * in first batch 8x3=24 tickers' data will be loaded in 6 calls, each call contains 3 tickers.
   * @param meta
   * @param batchIteration
   * @returns
   */
  makeParamsAndCall(meta, batchIteration) {
    this.isDataInProgress = true;
    const dataCalls = [];
    Object.keys(this.columnsPerAPIType).forEach((apiType) => {
      if (this.columnsPerAPIType[apiType].columns.length) {
        let urlParams = `currency=${meta.currency}`;
        this.columnsPerAPIType[apiType].columns.forEach((col) => {
          urlParams = `${urlParams}&${this.getParams(meta, col, apiType)}`;
        });

        const batchSize =
          this.columnsPerAPIType[apiType].batchSize[batchIteration];
        let tickerIndex = 0;
        const callCount = this.columnsPerAPIType[apiType].callCount;
        if (batchIteration === this.BATCH_SUBSEQUENT_ITERATION) {
          // for second set of calls, it should not start from first ticker, it should start from
          // (first batch's iteration x callCount)
          tickerIndex =
            this.columnsPerAPIType[apiType].batchSize[
              this.BATCH_FIRST_ITERATION
            ] * callCount;
        }
        for (
          let i = 0;
          i < callCount && tickerIndex < this._meta.tickerInfo.length;
          i++
        ) {
          let tickerParams = "";
          for (
            let j = 0;
            j < batchSize && tickerIndex < this._meta.tickerInfo.length;
            j++
          ) {
            const ticker = this._meta.tickerInfo[tickerIndex].ticker;
            tickerParams = `${tickerParams}&ticker=${ticker}`;
            tickerIndex++;
          }
          const urlParamsT = urlParams + tickerParams;
          dataCalls.push(this.dataCall(urlParamsT, apiType));
        }
      }
    });
    if (dataCalls.length === 0) {
      this.batchSubscription = this.batchStatus
        .pipe(take(1))
        .subscribe((status) => {
          if (status === "firstBatchCompleted") {
            this.batchStatus.next("secondBatchCompleted");
          }
        });
    }
    return dataCalls;
  }

  /**
   * converts rawData to rowData  needed by grid to show values
   * rawData is in the form
   * {
   *  tickerId: {
   *    coldId: {
   *      subColumns: {
   *        subColId: {object containing relevant values}
   *      }
   *    }
   *  }
   * }
   *
   * rowData is in the form
   * [
   *   {
   *     coldId: {
   *       subColumns: {
   *        subColId: {object containing relevant values}
   *       }
   *     }
   *   }
   * ]
   */
  pushToRowData() {
    this.gridData = {
      ...this.gridData,
      rowData: [],
    };
    this._meta.tickerInfo.forEach((tickerObj) => {
      this.gridData.rowData.push(this.rawData[tickerObj.ticker]);
    });
  }

  /**
   * finds column or subcolumn for the given id
   * @param meta
   * @param colId
   * @returns column or subcolumn object
   */
  findColOrSubColWithId(meta, colId, apiType): SubColumn | Column {
    let colOrSubCol: Column = null;
    for (const col of Object.values(meta.colInfo as Column)) {
      if (col.id === colId && col.api_type === apiType) {
        colOrSubCol = col;
        break;
      } else {
        const subCol = col.subheaders_info.find((subCol) => {
          return subCol.id === colId && subCol.api_type === apiType;
        });
        if (subCol) {
          colOrSubCol = subCol;
          break;
        }
      }
    }
    return colOrSubCol;
  }

  /**
   * generates api params for a given column/subColumn from its attributes
   * @param meta
   * @param colId
   * @returns
   */
  getParams(meta, colId, apiType) {
    let urlParams = "";
    const colOrSubCol = this.findColOrSubColWithId(meta, colId, apiType);
    const name = colOrSubCol.name;
    const id = colOrSubCol.id.toString().split("|")[0];

    if (name && id) {
      urlParams = `${name}=${id}&api_type=${apiType}`;
    }
    const extraInfo = (colOrSubCol as SubColumn).extras_info;
    if (extraInfo) {
      const extraInfoElm = extraInfo[0];
      extraInfoElm &&
        Object.keys(extraInfoElm).forEach((key) => {
          urlParams = `${urlParams}&${key}=${extraInfoElm[key]}`;
        });
    }
    return urlParams;
  }

  handleTickerOrderUpdate(tickerOrderedList) {
    this.updateTickerOrder.emit(tickerOrderedList);
  }

  setLoadingFalseAndcomputeAvgAndMedian() {
    const row = Object.keys(this.rawData);
    const marketAndEvCol = ["C2", "C3"];

    //Iterating for market cap and Ev Column
    for (let j = 0; j < marketAndEvCol.length; j++) {
      for (let k = 0; k < row.length; k++) {
        const _row = this.rawData[row[k]];
        if (_row[marketAndEvCol[j]]["isLoading"]) {
          this.rawData[row[k]][marketAndEvCol[j]] = {
            ...this.rawData[row[k]][marketAndEvCol[j]],
            isLoading: false,
            formatted: "-",
            raw: undefined,
          };
        }
      }
    }

    //Iterating for columns after Market and EV Cols hence starting with i=4
    for (let i = 4; i < this._meta.columns.length; i++) {
      const colHeader = this._meta.columns[i];

      let subColumns = [];
      if (this._meta.colInfo[colHeader].subheaders_info) {
        subColumns = this._meta.colInfo[colHeader].subheaders_info.map(
          (value) => value.id,
        );
      }

      //Iterating the sub columns and then for each ticker
      for (const subCol of subColumns) {
        for (let k = 0; k < row.length; k++) {
          const _row = this.rawData[row[k]];
          if (
            _row[colHeader].subColumns[subCol] &&
            _row[colHeader].subColumns[subCol]["isLoading"]
          ) {
            this.rawData[row[k]][colHeader].subColumns[subCol] = {
              ...this.rawData[row[k]][colHeader].subColumns[subCol],
              isLoading: false,
              formatted: "-",
              raw: undefined,
            };
          }
        }
      }
    }

    const avg = computeVsPeersGridAverage(this._meta, this.rawData);
    const median = computeVsPeersGridMedian(this._meta, this.rawData);
    this.gridData = {
      ...this.gridData,
      footerData: {
        average: avg,
        median: median,
        subColumnsWithoutLoader: SUB_COLUMNS_WITHOUT_LOADER,
      },
    };
  }

  handleOnDeleteTicker(tickerData) {
    // deleting it from rawData so that we dont need to make api call to refresh the grid
    // instead deleting the ticker info will do the required work
    delete this.rawData[tickerData.ticker];

    // deleting it from the meta data too so that the grid does not show an empty row for the deleted ticker
    // the _meta will also be updated in the parent component
    this._meta.tickerInfo = this._meta.tickerInfo.filter((_ticker) => {
      return _ticker.ticker !== tickerData.ticker;
    });

    this.pushToRowData();
    this.onDeleteTicker.emit(tickerData);
    this.setLoadingFalseAndcomputeAvgAndMedian();
  }
}
